// Demandes (D-031, D-075) : celles de la page publique (« Viens essayer ») et celles venues d'un
// lien d'équipe ou d'une amie. Le bureau voit tout, chaque coach celles de sa catégorie et de
// ses équipes. Trois décisions : « Dans l'effectif » (sa fiche, et son accès), « Essai d'abord »
// (invitée au prochain entraînement, la demande attend), « Refuser ».
import { decideJoin, invite, personInviteLink, setTrialStatus, trialRequests, type TrialRequest, type TrialStatus } from '../lib/api';
import { shortDate } from './format';
import { agree, clubGender, playerNoun, type TeamGender } from '../domain/labels';
import { type Ctx } from './context';
import { $$, html, mount, toast, type Html } from './dom';
import { inviteUrl, openShare } from './share';

const STATUS: Record<TrialStatus, string> = {
  nouveau: 'À traiter', contacte: 'À l’essai', venu: 'Venu essayer', inscrit: 'Dans l’effectif', sans_suite: 'Sans suite',
};
const clubIds = (ctx: Ctx) => ctx.clubs.map(c => c.id);
const canSee = (ctx: Ctx) => ctx.adminOf.length > 0 || ctx.staffOf.size > 0;
const open = (t: TrialRequest) => t.status === 'nouveau' || t.status === 'contacte' || t.status === 'venu';
const origin = (t: TrialRequest, teamName: string | null, g: TeamGender) =>
  t.source === 'friend' ? `${agree(g, 'invité', 'invitée')} par ${t.invited_name ?? 'un membre'}` : t.source === 'team' ? `lien des ${teamName ?? 'l’équipe'}` : 'page publique · Viens essayer';

/** Les équipes où cette demande peut entrer : la sienne, sinon celles de sa catégorie que j'encadre. */
function targets(ctx: Ctx, t: TrialRequest) {
  if (t.team_id) return ctx.full.teams.filter(x => x.id === t.team_id);
  const mine = ctx.full.teams.filter(x => ctx.full.staffOf.has(x.id) && x.club_id === t.club_id);
  const cat = mine.filter(x => t.category && x.category === t.category);
  return cat.length ? cat : mine;
}

function card(ctx: Ctx, t: TrialRequest): Html {
  const year = new Date().getFullYear();
  const team = ctx.full.teams.find(x => x.id === t.team_id) ?? null;
  const choices = targets(ctx, t);
  const g: TeamGender = team?.gender ?? 'X';
  return html`
    <li class="card trial ${t.status === 'nouveau' ? 'trial-new' : ''}" data-req="${t.id}">
      <div class="trial-head">
        <strong>${t.first_name}${t.last_name ? ` ${t.last_name}` : ''}</strong>
        <span class="muted small">${origin(t, team?.name ?? null, g)}</span>
        <span class="muted small trial-date">${shortDate(t.created_at.slice(0, 10))}</span>
      </div>
      <p class="small">${[team?.name ?? t.category, t.birth_year ? `année ${t.birth_year} · ${year - t.birth_year} ans` : null, t.contact_name ? `parent : ${t.contact_name}` : null].filter(Boolean).join(' · ')}</p>
      <p class="trial-contact">
        ${t.phone ? html`<a href="tel:${t.phone.replace(/[^0-9+]/g, '')}">${t.phone}</a>` : ''}
        ${t.email ? html`<a href="mailto:${t.email}">${t.email}</a>` : ''}
      </p>
      ${t.message ? html`<p class="trial-msg">« ${t.message} »</p>` : ''}
      ${open(t) ? html`
        ${t.status !== 'nouveau' ? html`<p class="small"><span class="chip chip-mute">${STATUS[t.status]}${t.trial_on ? ` · ${shortDate(t.trial_on)}` : ''}</span></p>` : ''}
        ${choices.length > 1 ? html`<label class="field"><span>Équipe</span><select data-team="${t.id}">${choices.map(c => html`<option value="${c.id}">${c.name}</option>`)}</select></label>` : ''}
        ${choices.length ? html`<div class="btn-row iv-decide">
          <button type="button" class="btn btn-primary btn-sm" data-guide="trial-member" data-dec="member" data-id="${t.id}">Dans l’effectif</button>
          ${t.status === 'nouveau' ? html`<button type="button" class="btn btn-ghost btn-sm" data-dec="trial" data-id="${t.id}">Essai d’abord</button>` : ''}
          <button type="button" class="btn btn-danger-ghost btn-sm" data-dec="refuse" data-id="${t.id}">Refuser</button>
        </div>` : html`<div class="seg seg-wrap" role="radiogroup" aria-label="Où en est la demande">${(['nouveau', 'contacte', 'venu', 'sans_suite'] as TrialStatus[]).map(s => html`
          <button type="button" role="radio" data-trial="${t.id}" data-st="${s}" aria-pressed="${String(s === t.status)}">${STATUS[s]}</button>`)}</div>`}`
        : html`<p class="small"><span class="chip ${t.status === 'inscrit' ? '' : 'chip-mute'}">${STATUS[t.status]}${t.status === 'inscrit' ? ' ✓' : ''}</span></p>`}
    </li>`;
}

/** Une liste de demandes avec ses décisions (page des demandes, page « Inviter l'équipe »). */
export function renderRequestList(el: HTMLElement, ctx: Ctx, list: TrialRequest[], reload: () => void): void {
  const order = (t: TrialRequest) => (t.status === 'nouveau' ? 0 : open(t) ? 1 : 2);
  const sorted = [...list].sort((a, b) => order(a) - order(b) || b.created_at.localeCompare(a.created_at));
  mount(el, html`<ul class="trial-list">${sorted.map(t => card(ctx, t))}</ul>`);
  for (const b of $$<HTMLButtonElement>(el, '[data-trial]')) b.addEventListener('click', async () => {
    try { await setTrialStatus(b.dataset.trial!, b.dataset.st as TrialStatus); toast(STATUS[b.dataset.st as TrialStatus]); reload(); }
    catch { toast('Mise à jour impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-dec]')) b.addEventListener('click', async () => {
    const t = list.find(x => x.id === b.dataset.id)!;
    const sel = el.querySelector<HTMLSelectElement>(`[data-team="${t.id}"]`);
    const teamId = sel?.value ?? targets(ctx, t)[0]?.id ?? null;
    const dec = b.dataset.dec as 'member' | 'trial' | 'refuse';
    const team = ctx.full.teams.find(x => x.id === teamId);
    b.disabled = true;
    try {
      const r = await decideJoin(t.id, dec, teamId);
      if (dec === 'refuse') { toast('Demande refusée'); reload(); return; }
      if (dec === 'trial') { toast(r.trial_on ? `${agree(team?.gender ?? 'X', 'Invité', 'Invitée')} à l’entraînement du ${shortDate(r.trial_on)}` : 'À l’essai : pas d’entraînement dans les 3 semaines'); reload(); return; }
      // Dans l'effectif : son accès, par e-mail s'il y en a un, sinon un lien à envoyer.
      if (r.email && r.person_id) {
        const sent = await invite(r.minor ? 'parent' : 'player', r.person_id, r.email);
        toast(sent.error ? 'Dans l’effectif. L’e-mail n’est pas parti : envoie son lien.' : `${t.first_name} est dans l’effectif · son accès part par e-mail`, sent.error ? 'ko' : 'ok');
        if (!sent.error) { reload(); return; }
      }
      reload();
      if (r.person_id) {
        const l = await personInviteLink(r.person_id, r.minor ? 'parent' : 'self');
        const url = inviteUrl(l.token);
        openShare(`L’accès de ${t.first_name}`,
          r.minor ? `${t.first_name} est dans l’effectif. Envoie ce lien à ses parents : un e-mail, un code, et c’est fait.`
                  : `${t.first_name} est dans l’effectif. Envoie-lui ce lien : un e-mail, un code, et c’est fait.`,
          r.minor ? `Bonjour, ${t.first_name} est dans l’effectif${team ? ` des ${team.name}` : ''} ! Votre accès à l’app du club : ${url} (le lien ne sert qu’une fois).`
                  : `Salut ${t.first_name} ! Bienvenue${team ? ` chez les ${team.name}` : ''}. Ton accès à l’app du club : ${url} (le lien ne sert qu’une fois).`);
      }
    } catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
  });
}

export async function renderTrials(root: HTMLElement, ctx: Ctx): Promise<void> {
  const list = canSee(ctx) ? await trialRequests(clubIds(ctx)).catch(() => [] as TrialRequest[]) : [];
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">Page publique · liens d’équipe · amis</p><h2>Essais et demandes</h2></header>
      ${list.length ? html`<div id="tr-list"></div>`
        : html`<div class="empty"><p class="empty-title">Aucune demande pour l’instant.</p><p class="muted">Elles arrivent du bouton « Viens essayer » de la page publique, du lien d’une équipe et des invitations des ${playerNoun(clubGender(ctx.teams), 2)}.</p></div>`}
      <p class="hint">Coordonnées données pour être recontacté au sujet de la demande, rien d’autre.</p>
    </section>`);
  const el = root.querySelector<HTMLElement>('#tr-list');
  if (el) renderRequestList(el, ctx, list, () => void renderTrials(root, ctx));
}

/** Accueil du bureau et des coachs : les demandes à traiter. */
export async function renderTrialCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!canSee(ctx)) return;
  const fresh = (await trialRequests(clubIds(ctx)).catch(() => [] as TrialRequest[])).filter(t => t.status === 'nouveau');
  if (!fresh.length) { mount(el, html``); return; }
  mount(el, html`
    <a class="card card-row trial-card" href="#/essais">
      <span class="play-thumb" aria-hidden="true"><span class="ti ti-hand"></span></span>
      <span class="card-main">
        <span class="eyebrow">Essais et demandes</span>
        <span class="card-title">${fresh.length} ${fresh.length > 1 ? 'demandes à traiter' : 'demande à traiter'}</span>
        <span class="muted small">${fresh.slice(0, 3).map(t => `${t.first_name}${t.source === 'friend' && t.invited_name ? ` (par ${t.invited_name})` : t.category ? ` (${t.category})` : ''}`).join(', ')}</span>
      </span>
      <span class="occ-go" aria-hidden="true">›</span>
    </a>`);
}
